import {
  DocumentClassificationType,
  DocumentRecord,
  DocumentRecordType,
  UploadBatchTerminalStatus,
} from '@/models/DocumentRecord';
import { AutoCompleteRule, Task, TaskGroup, TaskGroupView, TaskRole } from '@/models/Task';
import { DocumentReadiness } from '@/types/clientGroups';

export const HOA_REVIEW_TASK_TITLE = 'HOA Review';
export const SELLER_DISCLOSURES_REVIEW_TASK_TITLE = 'Review Seller Disclosures';
export const MARKET_INSIGHTS_REVIEW_TASK_TITLE = 'Review Market Insights';
export const MLS_DOCUMENT_CHECK_TASK_TITLE = 'Review MLS listing';
export const KICKOFF_TASK_TITLE = 'Schedule kick-off call';
export const LOCAL_TASK_FIXTURE_PREFIX = 'local-fixture:';
export const CONTINGENCY_REVIEW_TASK_TITLE = 'Review contingency options';
export const PRE_APPROVAL_TASK_TITLE = 'Obtain lender pre-approval';

export const hasRequiredOnboardingAnswer = (
  taskTitle: string,
  documentReadiness: DocumentReadiness | undefined,
  preApprovalLetterAvailable: boolean | undefined,
): boolean =>
  (taskTitle !== KICKOFF_TASK_TITLE || preApprovalLetterAvailable !== undefined) &&
  (taskTitle !== MLS_DOCUMENT_CHECK_TASK_TITLE || documentReadiness !== undefined);

const OBSOLETE_TASK_TITLES = new Set([
  'Frame repair-to-price ratio negotiation',
  'Agree on offer position and document it',
  'Review HomeDNA & inspection report',
]);

const DOCUMENT_REVIEW_ONLY_TITLES = new Set([
  'Review ConditionScore',
  'Review The Home',
  'Review insights',
  'Review Repair Planner',
]);

const NON_DILIGENCE_DOCUMENT_TYPES = new Set<DocumentClassificationType>([
  DocumentClassificationType.NHD,
  DocumentClassificationType.OTHER,
  DocumentClassificationType.PENDING,
]);

/** Uploaded diligence evidence is authoritative and automatically opens the document path. */
export const hasDiligenceDocuments = (records: DocumentRecord[] | undefined): boolean =>
  Boolean(
    records?.some(
      (record) =>
        record.type === DocumentRecordType.File &&
        (Boolean(record.inspectionReportId) ||
          Boolean(record.sellerDisclosureProcessedAt) ||
          Boolean(record.hoaProcessedAt) ||
          (record.documentType !== undefined &&
            !NON_DILIGENCE_DOCUMENT_TYPES.has(record.documentType))),
    ),
  );

export type DocumentReviewTaskState = 'missing' | 'processing' | 'attention' | 'ready';

const ATTENTION_STATUSES = new Set<UploadBatchTerminalStatus>([
  UploadBatchTerminalStatus.Failed,
  UploadBatchTerminalStatus.Incomplete,
  UploadBatchTerminalStatus.NeedsAttention,
]);

const documentState = (
  records: DocumentRecord[],
  isProcessed: (record: DocumentRecord) => boolean,
): DocumentReviewTaskState => {
  if (records.length === 0) return 'missing';
  if (records.some(isProcessed)) return 'ready';
  if (
    records.some(
      (record) =>
        record.uploadBatchTerminalStatus !== undefined &&
        ATTENTION_STATUSES.has(record.uploadBatchTerminalStatus),
    )
  ) {
    return 'attention';
  }
  return 'processing';
};

export const getSellerDisclosureTaskState = (
  records: DocumentRecord[] | undefined,
): DocumentReviewTaskState =>
  documentState(
    (records ?? []).filter(
      (record) => record.documentType === DocumentClassificationType.SELLER_DISCLOSURE,
    ),
    (record) => Boolean(record.sellerDisclosureManifest || record.sellerDisclosureProcessedAt),
  );

const isHOADocument = (record: DocumentRecord): boolean =>
  Boolean(record.hoaProcessedAt) || record.documentType?.startsWith('HOA_') === true;

export const hasHOADocuments = (records: DocumentRecord[] | undefined): boolean =>
  (records ?? []).some(isHOADocument);

export const getHOATaskState = (
  records: DocumentRecord[] | undefined,
  checklistShowsReceivedDocument: boolean,
): DocumentReviewTaskState => {
  const hoaRecords = (records ?? []).filter(isHOADocument);
  if (hoaRecords.length === 0 && checklistShowsReceivedDocument) return 'processing';
  return documentState(hoaRecords, (record) => Boolean(record.hoaProcessedAt));
};

export const formatReviewTaskTitle = (title: string): string => {
  if (title === HOA_REVIEW_TASK_TITLE) return 'Review: HOA';
  return title.startsWith('Review ') ? `Review: ${title.slice('Review '.length)}` : title;
};

export const ensureAdvisorReviewFixtures = (
  tasks: Task[],
  dealId: string,
  listingId: string | undefined,
): Task[] => {
  if (!listingId) return tasks;

  const currentTasks = tasks.filter((task) => !OBSOLETE_TASK_TITLES.has(task.title));
  const titles = new Set(currentTasks.map((task) => task.title));
  const fixtures: Task[] = [
    {
      id: `${LOCAL_TASK_FIXTURE_PREFIX}${listingId}:mls-document-check`,
      dealId,
      listingId,
      group: TaskGroup.Onboarding,
      groupLabel: 'Onboarding',
      groupOrder: 1,
      taskOrder: 3,
      title: MLS_DOCUMENT_CHECK_TASK_TITLE,
      subtitle:
        'Review listing details, agent remarks, offer timing, supplements, and available disclosures or reports',
      role: TaskRole.Advisor,
      autoCompleteRule: AutoCompleteRule.Manual,
      createdAt: 0,
      updatedAt: 0,
    },
    {
      id: `${LOCAL_TASK_FIXTURE_PREFIX}${listingId}:seller-disclosures-review`,
      dealId,
      listingId,
      group: TaskGroup.DueDiligenceReview,
      groupLabel: 'Due diligence review',
      groupOrder: 2,
      taskOrder: 7,
      title: SELLER_DISCLOSURES_REVIEW_TASK_TITLE,
      subtitle: 'Review the seller disclosure packet for material facts, defects, and advisories',
      role: TaskRole.Advisor,
      autoCompleteRule: AutoCompleteRule.Manual,
      createdAt: 0,
      updatedAt: 0,
    },
    {
      id: `${LOCAL_TASK_FIXTURE_PREFIX}${listingId}:hoa-review`,
      dealId,
      listingId,
      group: TaskGroup.DueDiligenceReview,
      groupLabel: 'Due diligence review',
      groupOrder: 2,
      taskOrder: 8,
      title: HOA_REVIEW_TASK_TITLE,
      subtitle: 'Review HOA documents, financial health, governance, rules, and property risks',
      role: TaskRole.Advisor,
      autoCompleteRule: AutoCompleteRule.Manual,
      createdAt: 0,
      updatedAt: 0,
    },
    {
      id: `${LOCAL_TASK_FIXTURE_PREFIX}${listingId}:market-insights-review`,
      dealId,
      listingId,
      group: TaskGroup.DueDiligenceReview,
      groupLabel: 'Due diligence review',
      groupOrder: 2,
      taskOrder: 9,
      title: MARKET_INSIGHTS_REVIEW_TASK_TITLE,
      subtitle: 'Review comparable properties, market momentum, pricing, and negotiation context',
      role: TaskRole.Advisor,
      autoCompleteRule: AutoCompleteRule.Manual,
      createdAt: 0,
      updatedAt: 0,
    },
    {
      id: `${LOCAL_TASK_FIXTURE_PREFIX}${listingId}:lender-pre-approval`,
      dealId,
      listingId,
      group: TaskGroup.OfferStrategy,
      groupLabel: 'Offer strategy',
      groupOrder: 3,
      taskOrder: 1,
      title: PRE_APPROVAL_TASK_TITLE,
      subtitle:
        'Complete lender review and obtain a current pre-approval letter before setting the offer price and terms',
      role: TaskRole.Client,
      autoCompleteRule: AutoCompleteRule.Manual,
      createdAt: 0,
      updatedAt: 0,
    },
    {
      id: `${LOCAL_TASK_FIXTURE_PREFIX}${listingId}:offer-strategy-review`,
      dealId,
      listingId,
      group: TaskGroup.OfferStrategy,
      groupLabel: 'Offer strategy',
      groupOrder: 3,
      taskOrder: 1.5,
      title: 'Schedule: Offer strategy review',
      subtitle:
        'Review offer price, contingencies, timing, and submission strategy before preparing the RPA',
      role: TaskRole.Client,
      bookingNamespace: 'offer-strategy',
      autoCompleteRule: AutoCompleteRule.Manual,
      createdAt: 0,
      updatedAt: 0,
    },
    {
      id: `${LOCAL_TASK_FIXTURE_PREFIX}${listingId}:offer-price`,
      dealId,
      listingId,
      group: TaskGroup.OfferStrategy,
      groupLabel: 'Offer strategy',
      groupOrder: 3,
      taskOrder: 2,
      title: 'Agree on offer price',
      subtitle: 'Review the market position and agree on the price to submit',
      role: TaskRole.Both,
      autoCompleteRule: AutoCompleteRule.Manual,
      createdAt: 0,
      updatedAt: 0,
    },
    {
      id: `${LOCAL_TASK_FIXTURE_PREFIX}${listingId}:contingency-contract-review`,
      dealId,
      listingId,
      group: TaskGroup.OfferStrategy,
      groupLabel: 'Offer strategy',
      groupOrder: 3,
      taskOrder: 3,
      title: 'Review property, loan, and appraisal contingencies',
      subtitle: 'Review the purpose, timing, and risk of each proposed contingency',
      role: TaskRole.Both,
      autoCompleteRule: AutoCompleteRule.Manual,
      createdAt: 0,
      updatedAt: 0,
    },
    {
      id: `${LOCAL_TASK_FIXTURE_PREFIX}${listingId}:submit-rpa`,
      dealId,
      listingId,
      group: TaskGroup.OfferStrategy,
      groupLabel: 'Offer strategy',
      groupOrder: 3,
      taskOrder: 4,
      title: 'Submit the RPA',
      subtitle: 'Prepare, sign, and submit the Residential Purchase Agreement',
      role: TaskRole.Both,
      autoCompleteRule: AutoCompleteRule.Manual,
      createdAt: 0,
      updatedAt: 0,
    },
    {
      id: `${LOCAL_TASK_FIXTURE_PREFIX}${listingId}:contingency-review`,
      dealId,
      listingId,
      group: TaskGroup.ContingencyReview,
      groupLabel: 'Contingency review',
      groupOrder: 4,
      taskOrder: 1,
      title: CONTINGENCY_REVIEW_TASK_TITLE,
      subtitle:
        'Consider removing contingencies, negotiating terms, extending the contingency period, or canceling the contract',
      role: TaskRole.Both,
      autoCompleteRule: AutoCompleteRule.Manual,
      createdAt: 0,
      updatedAt: 0,
    },
  ];

  return [...currentTasks, ...fixtures.filter((fixture) => !titles.has(fixture.title))];
};

const isVisibleTask = (
  task: Task,
  isHOAActive: boolean,
  isSellerDisclosuresActive: boolean,
  isDocumentReviewActive: boolean,
): boolean =>
  !OBSOLETE_TASK_TITLES.has(task.title) &&
  (!DOCUMENT_REVIEW_ONLY_TITLES.has(task.title) || isDocumentReviewActive) &&
  (task.group !== TaskGroup.ContingencyReview || isDocumentReviewActive) &&
  (task.title !== HOA_REVIEW_TASK_TITLE || isHOAActive) &&
  (task.title !== SELLER_DISCLOSURES_REVIEW_TASK_TITLE ||
    isSellerDisclosuresActive ||
    isDocumentReviewActive);

const ADVISOR_REVIEW_ORDER: Record<string, number> = {
  'Review ContextScore': 6,
  'Review The Area': 6,
  [SELLER_DISCLOSURES_REVIEW_TASK_TITLE]: 6.5,
  [HOA_REVIEW_TASK_TITLE]: 7,
  'Review insights': 8,
  'Review Repair Planner': 8,
  [MARKET_INSIGHTS_REVIEW_TASK_TITLE]: 9,
};

const byAdvisorReviewOrder = (a: Task, b: Task): number =>
  (ADVISOR_REVIEW_ORDER[a.title] ?? a.taskOrder) - (ADVISOR_REVIEW_ORDER[b.title] ?? b.taskOrder);

export const filterTasksForProperty = (
  tasks: Task[],
  isHOAActive: boolean,
  isSellerDisclosuresActive: boolean,
  isDocumentReviewActive = true,
): Task[] =>
  tasks
    .filter((task) =>
      isVisibleTask(task, isHOAActive, isSellerDisclosuresActive, isDocumentReviewActive),
    )
    .sort(byAdvisorReviewOrder);

export const filterTaskGroupsForProperty = (
  groups: TaskGroupView[],
  isHOAActive: boolean,
  isSellerDisclosuresActive: boolean,
  isDocumentReviewActive = true,
): TaskGroupView[] =>
  groups
    .map((group) => ({
      ...group,
      tasks: filterTasksForProperty(
        group.tasks,
        isHOAActive,
        isSellerDisclosuresActive,
        isDocumentReviewActive,
      ),
    }))
    .filter((group) => group.tasks.length > 0);
